import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Assessment } from "types/assessmentTypes";
import { buildAssessmentPdfPages } from "./buildAssessmentPdfPages";
import { generateAssessmentPdf } from "./generateAssessmentPdf";

export interface ExportPdfOptions {
  includePremium?: boolean;
  backgroundImage?: string;
}

const MIME_BY_EXT: Record<string, string> = {
  svg: "image/svg+xml",
  ico: "image/x-icon",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  webp: "image/webp",
  gif: "image/gif",
};

/**
 * Fetch an image from the API and convert it to a base64 data URL.
 * This prevents html-to-image from doing CORS requests during PDF capture.
 */
async function fileToDataUrl(
  name: string,
  apiUrl: string,
  originalName?: string
): Promise<string> {
  const res = await fetch(`${apiUrl}/file/${name}`);
  if (!res.ok) throw new Error(`HTTP ${res.status} for ${name}`);

  const blob = await res.blob();

  // اگر type خالی یا عمومی بود، از پسوند اسم اصلی حدس بزن
  let type = blob.type;
  if (!type || type === "application/octet-stream") {
    const ext = originalName?.split(".").pop()?.toLowerCase() ?? "";
    type = MIME_BY_EXT[ext] ?? "image/png";
  }
  const typedBlob = type === blob.type ? blob : new Blob([blob], { type });

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(typedBlob);
  });
}

/**
 * Ensure every processStep has an imageDataUrl (base64) so that
 * html-to-image doesn't attempt a network request during capture.
 */
async function enrichAssessmentImages(
  assessment: Assessment
): Promise<Assessment> {
  const apiUrl = process.env.REACT_APP_API_URL || "";
  const processSteps = assessment.company?.processSteps || [];

  if (!processSteps.length) return assessment;

  const enrichedSteps = await Promise.all(
    processSteps.map(async (step) => {
      if (!step.image?.name) return step;
      try {
        const dataUrl = await fileToDataUrl(
          step.image.name,
          apiUrl,
          step.image.originalName
        );
        return { ...step, imageDataUrl: dataUrl };
      } catch (err) {
        console.warn("[PDF] Failed to load step image:", step.image?.name, err);
        return step;
      }
    })
  );

  return {
    ...assessment,
    company: {
      ...assessment.company,
      processSteps: enrichedSteps,
    },
  };
}

export function useAssessmentPdfExport() {
  const { t, i18n } = useTranslation();
  const [isExporting, setIsExporting] = useState(false);
  const [progress, setProgress] = useState<{
    current: number;
    total: number;
  } | null>(null);

  const exportPdf = useCallback(
    async (assessment: Assessment, options?: ExportPdfOptions) => {
      if (isExporting) return;

      setIsExporting(true);
      setProgress({ current: 0, total: 1 });

      try {
        const lang = i18n.language;
        const locale = lang === "fa" ? "fa-IR" : lang === "ar" ? "ar" : "en-US";
        const dir: "rtl" | "ltr" =
          lang === "fa" || lang === "ar" ? "rtl" : "ltr";
        const company = assessment.company;
        const includePremium = options?.includePremium ?? true;
        const backgroundImage = options?.backgroundImage;

        const labels = {
          pages: {
            companyInfo: t("assessment.report.pdf.pages.companyInfo"),
            scoringTable: t("assessment.report.pdf.pages.scoringTable"),
            paei: t("assessment.report.pdf.pages.paei"),
            radar: t("assessment.report.pdf.pages.radar"),
            lifeCycle: t("assessment.report.pdf.pages.lifeCycle"),
            performance: t("assessment.report.pdf.pages.performance"),
            strategy: t("assessment.report.pdf.pages.strategy"),
            processSteps: t("assessment.report.pdf.pages.processSteps"),
          },
          company: {
            companyName: t("company.fields.name"),
            nationalId: t("company.fields.nationalId"),
            user: t("company.fields.user"),
            organization: t("organization.name"),
            industry: t("company.fields.industrySectorId"),
            establishedYear: t("assessment.report.pdf.company.foundationYear"),
            completedAt: t("assessment.report.pdf.company.completedAt"),
            pdfDownloadedAt: t("assessment.report.pdf.company.downloadDate"),
            employeeCount: t("assessment.report.pdf.company.employees"),
            companyType: t("company.fields.companyType"),
            companyStage: t("company.fields.companyStage"),
            licenseType: t("licenses.tier"),
          },
          companyValues: {
            companyName: company.name || "-",
            nationalId: company.nationalId || "-",
            user: company.user?.fullname || "-",
            organization: company.organization
              ? `${company.organization?.name} ${
                  company.organization?.province?.name || ""
                }`
              : "-",
            industry: company.industrySector?.key
              ? t(`industrySectors.${company.industrySector.key}`)
              : "-",
            establishedYear: company.foundationYear
              ? new Intl.DateTimeFormat(lang === "fa" ? "fa-IR" : "en-US", {
                  year: "numeric",
                }).format(new Date(company.foundationYear))
              : "-",
            completedAt: assessment.completedAt
              ? new Date(assessment.completedAt).toLocaleDateString(locale)
              : "-",
            pdfDownloadedAt: new Date().toLocaleDateString(locale),
            employeeCount: company.totalStaffCount?.total
              ? String(company.totalStaffCount?.total)
              : "-",
            companyType: company.companyType
              ? t(`company.companyTypes.${company.companyType}`)
              : "-",
            companyStage: company.companyStage
              ? t(`company.companyStages.${company.companyStage}`)
              : "-",
            licenseType: assessment?.license?.tier
              ? t(`licenses.tiers.${assessment.license?.tier}`)
              : "-",
          },
          scoringHeaders: {
            row: t("table.row"),
            criteria: t("assessment.report.criteria"),
            likert: t("assessment.report.likertPercent"),
            objective: t("assessment.report.objectivePercent"),
            factor: t("assessment.report.correctionFactor"),
            corrected: t("assessment.report.correctedPercent"),
            interpretation: t("assessment.report.interpretations"),
          },
          statusLabels: {
            needsImprovement: t("assessment.report.status.needsImprovement"),
            average: t("assessment.report.status.average"),
            good: t("assessment.report.status.good"),
          },
          getCriteriaLabel: (index: number) => t(`criteria.${index}`),
          radarTitle: t("assessment.report.radarChart"),
          radarDesc: t("assessment.report.radarChartdesc"),
        };

        // Pre-load all processStep images as base64 data URLs
        const enrichedAssessment = await enrichAssessmentImages(assessment);

        const pages = buildAssessmentPdfPages(enrichedAssessment, labels, {
          includePremium,
          backgroundImage,
          dir,
          scoringRowsPerPage: 18,
        });

        await generateAssessmentPdf({
          fileName: `report-assessment.pdf`,
          pages,
          onProgress: (current, total) => setProgress({ current, total }),
        });
      } finally {
        setIsExporting(false);
        setProgress(null);
      }
    },
    [i18n.language, isExporting, t]
  );

  return { exportPdf, isExporting, progress };
}
